import { useMutation } from '@tanstack/react-query';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { usePanel } from 'contexts/panelContext';
import { RefObject, useEffect, useRef, useState } from 'react';
import { KnowledgeBaseArticlePresentationResult } from 'types/KnowledgeBase';

type UseArticleReadOptions = {
	enabled: boolean;
	scrollContainerRef?: RefObject<HTMLDivElement>;
	articleData?: KnowledgeBaseArticlePresentationResult;
	applicationId?: number;
	onRead?: () => void;
};

export const useArticleRead = ({
	enabled,
	scrollContainerRef,
	articleData,
	applicationId,
	onRead,
}: UseArticleReadOptions) => {
	const articleContentId = articleData?.articleContent?.id;
	const initialReaded = articleData?.info?.flag?.readed;
	const visitId = articleData?.info?.visitId;

	const { currentApplicationData } = usePanel();
	const resolvedAppId = applicationId ?? currentApplicationData?.id;

	const [readed, setReaded] = useState(initialReaded || false);
	const calledRef = useRef(false);

	// Sync initial readed state when articleData changes
	useEffect(() => {
		setReaded(initialReaded || false);
		calledRef.current = false;
	}, [initialReaded, articleContentId]);

	const { mutate: readMutate } = useMutation(
		() =>
			knowledgeBaseApi.application.readArticle(Number(articleContentId), Number(resolvedAppId), Number(visitId)),
		{
			onSuccess: (result) => {
				if (result) {
					setReaded(true);
				}
				onRead?.();
			},
		}
	);

	useEffect(() => {
		if (!enabled || readed || calledRef.current) {
			return undefined;
		}
		if (!articleContentId || !resolvedAppId || !visitId) {
			return undefined;
		}

		let activeEl: HTMLDivElement | null = null;
		let timerId: ReturnType<typeof setTimeout> | null = null;
		let scrollHandler: (() => void) | null = null;

		const doRead = () => {
			if (calledRef.current) return;
			calledRef.current = true;
			readMutate();
		};

		// Wait 1s for content to render, then check scroll
		timerId = setTimeout(() => {
			const el = scrollContainerRef?.current;
			if (!el) {
				// No scroll container — just wait 5s and mark as read
				timerId = setTimeout(doRead, 5000);
				return;
			}
			activeEl = el;

			const hasScroll = el.scrollHeight - el.clientHeight > 50;

			if (!hasScroll) {
				// Short content — wait 5s and mark as read
				timerId = setTimeout(doRead, 4000); // 1s already waited
			} else {
				// Long content — wait for 80% scroll AND 5s elapsed
				let scrolledEnough = false;
				let timerDone = false;

				scrollHandler = () => {
					const pct = (el.scrollTop + el.clientHeight) / el.scrollHeight;
					if (pct >= 0.8) {
						scrolledEnough = true;
						if (timerDone) {
							doRead();
						}
					}
				};
				el.addEventListener('scroll', scrollHandler);

				timerId = setTimeout(() => {
					timerDone = true;
					// Check if already scrolled enough
					const pct = (el.scrollTop + el.clientHeight) / el.scrollHeight;
					if (pct >= 0.8) {
						scrolledEnough = true;
					}
					if (scrolledEnough) {
						doRead();
					}
				}, 4000); // 1s already waited
			}
		}, 1000);

		return () => {
			if (timerId) clearTimeout(timerId);
			if (scrollHandler && activeEl) {
				activeEl.removeEventListener('scroll', scrollHandler);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, readed, articleContentId, resolvedAppId, visitId]);

	return { readed };
};
